import Contact from '../../Contact';
import Entry from '../../Entry';
import EntryProcedure from '../../EntryProcedure';
import Horse from '../../Horse';
import { ContactModel } from '../../../../types/Contacts';
import { EntryModel } from '../../../../types/Entries';
import { EntryProcedureModel } from '../../../../types/EntryProcedure';
import { PreparedDataOnInvoiceUpdate } from '../../../../types/Invoice';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { toPreparedDataItems } from './common';
import {
    appendPreparedBumpEntryProcedures,
    appendPreparedBumpHorses,
} from './invoiceAdd';

const fetchBumpRelatedElements = async ({
    invoiceId,
    entryService,
    entryProcedureService,
    contactService,
    contactId,
}: {
    invoiceId: string;
    entryService: Entry;
    entryProcedureService: EntryProcedure;
    contactService: Contact;
    contactId: string | null;
}) => {
    const entries = await entryService.getByParam('invoice_id', invoiceId);

    const entryProceduresNested = await Promise.all(
        entries.map((entry) =>
            entryProcedureService.getByParam('entry_id', entry.id),
        ),
    );

    const horsesIdsToBump = entries
        .map((entry) => entry.horseId)
        .filter((value, index, self) => self.indexOf(value) === index);

    const contact = contactId ? await contactService.getByID(contactId) : null;

    return {
        entries,
        entryProcedures: entryProceduresNested.flatMap((items) => items),
        horsesIdsToBump,
        contact,
    };
};

const prepareBumpRelatedUpdates = async ({
    entries,
    entryProcedures,
    horsesIdsToBump,
    contact,
    entryService,
    entryProcedureService,
    horseService,
    contactService,
}: {
    entries: EntryModel[];
    entryProcedures: EntryProcedureModel[];
    horsesIdsToBump: string[];
    contact: ContactModel | null;
    entryService: Entry;
    entryProcedureService: EntryProcedure;
    horseService: Horse;
    contactService: Contact;
}) => {
    const [entriesToBump, entryProceduresToBump, horsesToBump, contactToBump] =
        await Promise.all([
            entryService.prepareBumpUpdates(entries.map((entry) => entry.id)),
            entryProcedureService.prepareBumpUpdates(
                entryProcedures.map((entryProcedure) => entryProcedure.id),
            ),
            horseService.prepareBumpUpdates(horsesIdsToBump),
            contact
                ? contactService.prepareBumpUpdate(contact.id)
                : Promise.resolve(null),
        ]);

    return {
        entriesToBump,
        entryProceduresToBump,
        horsesToBump,
        contactToBump,
        contact,
    };
};

const appendPreparedBumpEntries = (
    preparedData: PreparedDataOnInvoiceUpdate[],
    bumpData: { entriesToBump: EntryModel[] },
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(bumpData.entriesToBump, 'entries', 'update'),
    ];
};

const appendPreparedBumpContact = (
    preparedData: PreparedDataOnInvoiceUpdate[],
    bumpData: { contactToBump: ContactModel | null },
) => {
    if (!bumpData.contactToBump) {
        return preparedData;
    }

    return [
        ...preparedData,
        ...toPreparedDataItems([bumpData.contactToBump], 'contacts', 'update'),
    ];
};

const logBumpRelatedDBActions = ({
    entriesToBump,
    entryProceduresToBump,
    contact,
    logDBAction,
}: {
    entriesToBump: EntryModel[];
    entryProceduresToBump: EntryProcedureModel[];
    contact: ContactModel | null;
    logDBAction: DBWriterLoggerFunction;
}) => {
    logDBAction({
        message: 'Entries bump',
        modelName: 'entries',
        payload: entriesToBump,
    });

    logDBAction({
        message: 'Bump entry procedures',
        modelName: 'entry_procedures',
        payload: entryProceduresToBump,
    });

    if (contact) {
        logDBAction({
            message: 'Update contact field firstName',
            modelName: 'contacts',
            payload: contact,
        });
    }
};

export {
    appendPreparedBumpContact,
    appendPreparedBumpEntries,
    appendPreparedBumpEntryProcedures,
    appendPreparedBumpHorses,
    fetchBumpRelatedElements,
    logBumpRelatedDBActions,
    prepareBumpRelatedUpdates,
};
